import React, { useState } from 'react';
import { Button, LabeledInput, RuleCheck } from 'Components';

interface PasswordControl {
  regex: RegExp;
  ruleText: string;
  tooltip?: boolean;
}

const atLeastTenCharacters: PasswordControl = {
  regex: /.{10,}/,
  ruleText: 'Au moins 10 caractères',
};

const atLeastOneUpperCaseLetter: PasswordControl = {
  regex: /[A-Z]+/,
  ruleText: 'Au moins une majuscule',
};

const atLeastOneLowerCaseLetter: PasswordControl = {
  regex: /[a-z]+/,
  ruleText: 'Au moins une minuscule',
};

const atLeastOneDigit: PasswordControl = {
  regex: /\d+/,
  ruleText: 'Au moins un chiffre',
};

const atLeastOneSpecialCharacter: PasswordControl = {
  regex: /\W+/,
  ruleText: 'Au moins un caractère spécial',
  tooltip: true,
};

const passwordControls: PasswordControl[] = [
  atLeastTenCharacters,
  atLeastOneUpperCaseLetter,
  atLeastOneLowerCaseLetter,
  atLeastOneDigit,
  atLeastOneSpecialCharacter,
];

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  placeholder?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const PasswordCreation: React.FC<Props> = ({ placeholder, onChange, ...rest }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState<string>();
  const [rulesDisplayed, setRulesDisplayed] = useState(false);
  const errorMessage = 'Votre mot de passe doit respecter les conditions ci-dessous';

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const bigPattern = new RegExp(
    passwordControls
      .map(({ regex }) => regex)
      .map((regex) => regex.source)
      .map((stringRegex) => '(?=.*' + stringRegex + ')')
      .join(''),
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    event.target.setCustomValidity('');
    return onChange && onChange(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const nextFocused = event.relatedTarget as HTMLElement | null;
    const blurToButton = Boolean(nextFocused?.closest('button'));

    const isValidPattern = bigPattern.test(event.currentTarget.value);
    (event.target as HTMLInputElement).setCustomValidity(isValidPattern ? '' : errorMessage);

    if (blurToButton) {
      return;
    }

    setRulesDisplayed(false);
  };

  return (
    <>
      <span className="flex relative items-center">
        <LabeledInput
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder}
          name="password"
          onChange={handleChange}
          onFocus={() => setRulesDisplayed(true)}
          onBlur={handleBlur}
          className="pr-28"
          onInvalid={(event: React.FormEvent<HTMLInputElement>) => (event.target as HTMLInputElement).setCustomValidity(errorMessage)}
          specialPattern={bigPattern.source}
          {...rest}
        />
        <span className="absolute left-auto right-0 p-1.5 top-0">
          <Button onClick={togglePassword} type="button" variant="tertiary">
            {showPassword ? 'Masquer' : 'Afficher'}
          </Button>
        </span>
      </span>
      {rulesDisplayed && (
        <div className="flex flex-col bg-whitesmoke p-4 pt-2.5 pb-2.5 rounded text-sm text-dark-gray">
          <div className="font-semibold mb-2">Votre mot de passe doit respecter les conditions suivantes :</div>
          {passwordControls.map(({ ruleText, regex, tooltip }) => (
            <RuleCheck key={ruleText} ruleText={ruleText} regex={regex} input={inputValue} tooltip={tooltip === true} />
          ))}
        </div>
      )}
    </>
  );
};
